/**
 * Browser detection helpers.
 *
 * The ServiceBox transfer (local proxy on localhost) is only validated on Chrome /
 * Chromium-based browsers. Safari and Firefox handle the localhost proxy fetch and
 * mixed content differently and can break the transfer — we advise switching to Chrome.
 */

/** True for Chrome and Chromium-based browsers (Edge, Brave, Opera, Vivaldi, Chromium). */
export function isChromiumBrowser(): boolean {
  if (typeof navigator === "undefined") return true; // SSR / unknown — don't warn
  const ua = navigator.userAgent || "";
  // Chromium engines all carry a Chrome/Chromium token (Edg/OPR are Chromium too).
  // iOS "Chrome" (CriOS) is actually WebKit, so it is intentionally excluded.
  const chromium = /Chrome\/|Chromium\//.test(ua) || /Edg\//.test(ua) || /OPR\//.test(ua);
  // Exclude iOS WebKit wrappers that also match nothing above but be safe against Firefox/Safari.
  const isFirefox = /Firefox\//.test(ua) || /FxiOS\//.test(ua);
  return chromium && !isFirefox;
}

/** True on iOS / iPadOS (incl. iPadOS 13+ which reports as "Macintosh"). */
export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const maxTouch = (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints || 0;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouch > 1);
}

/**
 * True for embedded in-app browser WebViews (WhatsApp, Instagram, Facebook,
 * Messenger, TikTok, LinkedIn, Snapchat, WeChat, …).
 *
 * These WebViews — most notably WhatsApp's WKWebView on iOS — often block
 * camera access from a `<input type="file">`, giving a black camera screen so
 * the user can only pick from the photo library. Detect it to advise opening
 * the page in the real browser (Safari) instead.
 */
export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /(FBAN|FBAV|FB_IAB|Instagram|WhatsApp|Line\/|Twitter|TikTok|musical_ly|Snapchat|LinkedInApp|MicroMessenger|GSA\/)/i.test(ua);
}

/**
 * True on a phone/tablet — i.e. a device that has a camera worth offering.
 *
 * Used to decide whether to show the "photo or file" choice before opening a
 * file input: on desktop the plain file picker is the only sensible answer.
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const maxTouch = (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints || 0;
  return /Android|iPhone|iPad|iPod/i.test(ua) || isIOS() || maxTouch > 1;
}
